import React from "react";
import { Switch, Route, Redirect } from "react-router-dom";

import { Layout } from "./layout/Layout";
import ForgotPassword from "./module/auth/ForgotPassword";
// PAGES
import Login from "./module/auth/Login";
import BiReport from "./module/bi-report/View";
import Document from "./module/document/View";
import MerchList from "./module/merchandiser/MerchList";
import MerchandicerView from "./module/merchandiser/Page";
import OrdersList from "./module/orders/List";
import OrdersPage from "./module/orders/Page";
import OverviewView from "./module/overview/View";
import PhotoReviewPage from "./module/photo-review/View";
import { PointsResourceList, PointsResourcePage } from "./module/points";
import PointsList from "./module/points/List";
import Profile from "./module/profile/Edit";
import Help from "./module/help/View";
import AnswerList from "./module/answer/List";
import AnswerPage from "./module/answer/Page";
import { TaskList } from "./module/task/List";
import TaskPage from "./module/task/Page";
import Import from "./module/import/Import";
import Uploads from "./module/import/Uploads";
import ReturnsList from "./module/returns/List";
import ReturnsPage from "./module/returns/Page";
import CounterpartiesList from "./module/counterparties/List";
import CounterpartiesPage from "./module/counterparties/Page";
import SupervisorList from "./module/supervisor/List";
import SupervisorPage from "./module/supervisor/Page";
import VisitList from "./module/visit/List";
import VisitView from "./module/visit/View";
import Tracking from "./module/tracking/View";

import { User } from "./util/permission";
import { getStore } from "./util/redux/store";

const PrivateRoute = ({ component: Component, ...rest }) => {
  let me = getStore().getState().me;

  return <Route {...rest} render={props => (me ? <Component {...props} {...rest} /> : <Redirect to="/login" />)} />;
};

/**
 * A route only a supervisor manager may open, matching that row's `role` in `AppNav.MENU`.
 *
 * The import screens were gated by a hand-rolled, untranslated, hardcoded-English "Page Not Found"
 * block inside the page itself. A redirect belongs at the routing layer and says the true thing:
 * this account has no such page, not "this page does not exist".
 *
 * ⚠ The API is LOOSER than this: `tasks/bulk_create/`, `assortment_matrix/bulk_create/` and
 * `actions/` all permit `IsSupervisor | IsSupervisorManager`. This preserves the stricter
 * behaviour the old screen had rather than quietly widening access; whether a plain supervisor
 * should be able to import is a product decision.
 */
const ManagerOnly = Component => {
  const Gated = props => (getStore().getState().me?.role === "supervisor_manager" ? <Component {...props} /> : <Redirect to="/" />);
  Gated.displayName = `ManagerOnly(${Component.displayName || Component.name || "Component"})`;
  return Gated;
};

/**
 * A route only open when the company has bought the named feature (`Company.features`).
 * Mirrors `ManagerOnly`'s shape and its redirect-to-home behaviour — this project has no 404 page,
 * so every other closed-off route (`/import`, `/uploads`) already redirects rather than 404s.
 *
 * Deliberately its own check, not merged with `ManagerOnly`: role and feature are independent
 * questions, matching `AppNav.js`'s `hasFeature` alongside (not inside) `hasRole`.
 */
const FeatureOnly = feature => Component => {
  const Gated = props =>
    getStore()
      .getState()
      .me?.company?.features?.includes(feature) ? (
      <Component {...props} />
    ) : (
      <Redirect to="/" />
    );
  Gated.displayName = `FeatureOnly(${feature})(${Component.displayName || Component.name || "Component"})`;
  return Gated;
};

const AppRouter = () => {
  return (
    <Switch>
      <Route exact path="/login" component={Login} />
      <Route exact path="/forgot-password" component={ForgotPassword} />

      <Layout>
        <PrivateRoute exact path="/">
          <Redirect to="/merchandiser" />
        </PrivateRoute>
        <PrivateRoute exact path="/overview" component={User(OverviewView)} />
        <PrivateRoute exact path="/profile" component={User(Profile)} />
        <PrivateRoute exact path="/help" component={User(Help)} />

        {/*MERCHANDISER*/}
        <PrivateRoute exact path="/merchandiser" component={User(MerchList)} />
        <PrivateRoute exact path="/merchandiser/:id" component={User(MerchandicerView)} />

        {/*TASK*/}
        <PrivateRoute exact path="/task" component={User(TaskList)} />

        {/* :type is point | network | point_group. The last two are FAN-OUT creates — the server
            turns either into one task per point — so they land back on the list, not on a record. */}
        <PrivateRoute exact path="/task/new/:type" component={User(TaskPage)} />
        <PrivateRoute exact path="/task/:id" component={User(TaskPage)} />
        {/* The mode rides in the query string as `?edit=1` (spec/03), so these two paths are
            redirects. They were dead before: the page read the mode from `location.hash`, so
            /task/5/edit rendered the view and only /task/5#edit edited. Existing links and
            bookmarks still work. */}
        <Route exact path="/task/:id/edit" render={({ match }) => <Redirect to={`/task/${match.params.id}?edit=1`} />} />
        <Route exact path="/task/:id/view" render={({ match }) => <Redirect to={`/task/${match.params.id}`} />} />

        {/*  ANSWERS — the verdict queue. `/task-review` was the route while the screen was a list
             plus a full-screen overlay with no URL of its own; the record has a real page now, and
             the nav has said "Answers" all along. The old path is kept as a redirect for
             bookmarks. */}
        <PrivateRoute exact path="/answers" component={User(AnswerList)} />
        <PrivateRoute exact path="/answers/:id" component={User(AnswerPage)} />
        <Route exact path="/task-review" render={({ location }) => <Redirect to={`/answers${location.search}`} />} />

        {/*  IMPORT — was `/tasks-import/:id`, where `:id` was not a record but a TAB INDEX into one
             MUI <Tabs>. Three of those four tabs were the same flow copy-pasted and are now one
             page with a kind selector; the fourth, "My uploads", is its own list. The old paths
             are kept as redirects for bookmarks, as `/task-review` -> `/answers` was. */}
        <PrivateRoute exact path="/import" component={User(ManagerOnly(Import))} />
        <PrivateRoute exact path="/uploads" component={User(ManagerOnly(Uploads))} />
        <Route exact path="/tasks-import" render={() => <Redirect to="/import" />} />
        <Route exact path="/tasks-import/:id" render={({ match }) => <Redirect to={match.params.id === "3" ? "/uploads" : "/import"} />} />

        {/*  PHOTO REVIEW */}
        <PrivateRoute path="/photo-review" component={User(PhotoReviewPage)} />
        {/*<PrivateRoute exact path="/photo-review/:id" component={User(PhotoReviewPage)} />*/}
        {/*<PrivateRoute exact path="/photo-review/:id/edit" component={User(PhotoReviewPage)} />*/}
        {/*<PrivateRoute exact path="/photo-review/:id/view" component={User(PhotoReviewPage)} />*/}

        {/* DOCUMENTS */}
        <PrivateRoute exact path="/document" component={User(Document)} />

        {/* BI-REPORT */}
        <PrivateRoute exact path="/bi-report" component={User(BiReport)} />

        {/* Routes are edited on the merchandiser they belong to (`/merchandiser/:id`, Route tab).
            The standalone `/route` list and editor were the last screens on the pre-redesign
            EntityTable stack, carried no nav entry and were linked from nowhere. */}

        {/*VISIT */}
        <PrivateRoute exact path="/visit" component={User(VisitList)} />
        <PrivateRoute exact path="/visit/:id" component={User(VisitView)} />

        {/* ONLINE TRACKING — where the team is for a given day, derived from visits + the visited
            point's coordinates. `role: ["all"]` matches the MENU row: the visits endpoint already
            scopes a plain supervisor to `request.user.merchandisers` server-side, so there is
            nothing for ManagerOnly to add. */}
        <PrivateRoute exact path="/tracking" component={User(Tracking)} />

        {/* POINTS ADMIN */}
        {/* /points is the bespoke, data-quality list (docs/design/points.html); the other eight
            admin entities still render through the generic ResourceList. /points/:id and the
            legacy /point aliases stay on the generic ResourcePage editor. */}
        <PrivateRoute exact path="/points" component={User(PointsList)} />
        <PrivateRoute exact path="/points/:id" component={User(PointsResourcePage("points"))} />
        <PrivateRoute exact path="/networks" component={User(PointsResourceList("networks"))} />
        <PrivateRoute exact path="/networks/:id" component={User(PointsResourcePage("networks"))} />
        <PrivateRoute exact path="/categories" component={User(PointsResourceList("categories"))} />
        <PrivateRoute exact path="/categories/:id" component={User(PointsResourcePage("categories"))} />
        <PrivateRoute exact path="/countries" component={User(PointsResourceList("countries"))} />
        <PrivateRoute exact path="/countries/:id" component={User(PointsResourcePage("countries"))} />
        <PrivateRoute exact path="/counties" component={User(PointsResourceList("counties"))} />
        <PrivateRoute exact path="/counties/:id" component={User(PointsResourcePage("counties"))} />
        <PrivateRoute exact path="/localities" component={User(PointsResourceList("localities"))} />
        <PrivateRoute exact path="/localities/:id" component={User(PointsResourcePage("localities"))} />
        <PrivateRoute exact path="/point_groups" component={User(PointsResourceList("point_groups"))} />
        <PrivateRoute exact path="/point_groups/:id" component={User(PointsResourcePage("point_groups"))} />
        <PrivateRoute exact path="/surveys" component={User(PointsResourceList("surveys"))} />
        <PrivateRoute exact path="/surveys/:id" component={User(PointsResourcePage("surveys"))} />

        {/* Legacy /point aliases -> new points-admin, so existing deep links (task/visit/review tables) keep working */}
        <PrivateRoute exact path="/point" component={User(PointsResourceList("points"))} />
        <PrivateRoute exact path="/point/:id" component={User(PointsResourcePage("points"))} />

        {/* PERSONNEL MANAGEMENT */}
        {/* Both the list and the detail are their own pages now (redesign). */}
        <PrivateRoute exact path="/supervisors" component={User(SupervisorList)} />
        <PrivateRoute exact path="/supervisors/:id" component={User(SupervisorPage)} />

        {/* SALES — read-only lists; the counterparty detail also allows a partial PATCH. All gated
            on Company.features containing "orders" — there is no separate "returns" or
            "counterparties" flag; all three ride on the same one. */}
        <PrivateRoute exact path="/sales/orders" component={User(FeatureOnly("orders")(OrdersList))} />
        <PrivateRoute exact path="/sales/orders/:id" component={User(FeatureOnly("orders")(OrdersPage))} />
        <PrivateRoute exact path="/sales/returns" component={User(FeatureOnly("orders")(ReturnsList))} />
        <PrivateRoute exact path="/sales/returns/:id" component={User(FeatureOnly("orders")(ReturnsPage))} />
        <PrivateRoute exact path="/sales/counterparties" component={User(FeatureOnly("orders")(CounterpartiesList))} />
        <PrivateRoute exact path="/sales/counterparties/:id" component={User(FeatureOnly("orders")(CounterpartiesPage))} />
      </Layout>
    </Switch>
  );
};

export default AppRouter;
